// Employee profile & memory (prototype scene 19): what they remember (add / forget), job instructions, which
// company tools they may use and which ask the boss first, the AI model, voice and language.
import { useEffect, useState } from 'react';
import Avatar from '../Avatar';
import { getAgent, patchAgent, addMemory, forgetMemory, getTools, errorText } from '../api';
import { useLoad, usd, when, MODEL_LABELS } from '../fmt';

const VOICES = { female_warm: 'Female · warm', female_clear: 'Female · clear', male_calm: 'Male · calm' };
const LANGS = { same_as_boss: 'Same as the boss', bn: 'Bangla', en: 'English' };
const RULES = { auto: 'Can do alone', ask: 'Asks you first', off: 'Not allowed' };

function Select({ value, options, label, onChange }) {
  return (
    <select aria-label={label} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      {value && !options[value] && <option value={value}>{value}</option>}
    </select>
  );
}

export default function Profile({ agentId, onTalk, onBack, onSaved, onRemoved }) {
  const { data: a, error, loading, reload } = useLoad(() => getAgent(agentId), [agentId]);
  const [draft, setDraft] = useState(null); // edited fields only
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [newFact, setNewFact] = useState('');
  const [forEveryone, setForEveryone] = useState(false);
  const [memError, setMemError] = useState(null);
  const [allTools, setAllTools] = useState(null);
  const [addTool, setAddTool] = useState('');
  useEffect(() => { getTools().then(setAllTools, () => setAllTools([])); }, []);
  // Only a different employee clears the edits; adding or forgetting a memory reloads but keeps them.
  useEffect(() => { setDraft(null); setSaveError(null); }, [agentId]);

  if (error) return <div className="err">{errorText(error)}</div>;
  if (loading && !a) return <div className="panel empty">Loading…</div>;
  if (!a) return null;

  const d = draft || {};
  const val = (k) => (d[k] !== undefined ? d[k] : a[k]);
  const rule = (t) => (d.tools && d.tools[t.name] !== undefined ? d.tools[t.name] : t.rule);
  const set = (k, v) => setDraft((x) => ({ ...(x || {}), [k]: v }));
  const setRule = (name, v) => setDraft((x) => ({ ...(x || {}), tools: { ...((x && x.tools) || {}), [name]: v } }));
  const dirty = !!draft && Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true); setSaveError(null);
    try {
      await patchAgent(a.id, draft);
      setDraft(null);
      await reload();
      onSaved && onSaved(`${a.name}'s profile is saved.`);
    } catch (err) {
      setSaveError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!newFact.trim()) return;
    setMemError(null);
    try {
      await addMemory(a.id, newFact.trim(), forEveryone);
      setNewFact(''); setForEveryone(false);
      await reload();
      onSaved && onSaved(forEveryone ? 'Saved. The whole office will remember it.' : `Saved to ${a.name}'s memory.`);
    } catch (err) { setMemError(errorText(err)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${a.name} from the office? Their desk becomes free. (Their history stays.)`)) return;
    try { await patchAgent(a.id, { active: false }); onRemoved && onRemoved(a.name); } catch (err) { setSaveError(errorText(err)); }
  };
  const forget = async (m) => {
    setMemError(null);
    try { await forgetMemory(m.id); await reload(); onSaved && onSaved('Forgotten.'); } catch (err) { setMemError(errorText(err)); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="panel phead">
        <Avatar agent={a} size="lg" />
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2>{a.name}</h2>
          <div style={{ color: 'var(--muted)', fontWeight: 600 }}>{a.role}</div>
          <p style={{ margin: '6px 0 0', maxWidth: '60ch' }}>{a.description}</p>
          <div className="stats">
            <div><b>{a.stats.tasksWeek}</b><span>Tasks this week</span></div>
            <div><b>{a.stats.approvedWeek}</b><span>Approved</span></div>
            <div><b>{a.stats.rejectedWeek}</b><span>Rejected</span></div>
            <div><b>{usd(a.stats.costWeekUSD)}</b><span>AI cost this week</span></div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="aio-btn pri" onClick={() => onTalk(a.id)}>Talk to {a.name}</button>
          <button className="aio-btn" onClick={onBack}>Back to office</button>
          <button className="aio-btn ghost danger" onClick={remove}>Remove from office</button>
        </div>
      </div>
      <div className="prof">
        <div className="panel">
          <h3>What {a.name} remembers</h3>
          {a.memories.length
            ? a.memories.map((m) => (
              <div className="mem" key={m.id}>
                <p>{m.fact}<br /><span className="src">{m.source === 'boss' ? 'Told by you' : 'Learned from past work'}{m.shared ? ' · whole office' : ''} · {when(m.createdAt)}</span></p>
                <button className="aio-btn ghost" onClick={() => forget(m)} aria-label="Forget this">Forget</button>
              </div>
            ))
            : <p style={{ color: 'var(--muted)', margin: 0 }}>Nothing yet. Tell {a.name} "remember …" in the chat, or add it here.</p>}
          <form className="addmem" onSubmit={add}>
            <input value={newFact} onChange={(e) => setNewFact(e.target.value)} placeholder="Add something to remember" aria-label="New memory" />
            <button className="aio-btn" type="submit" disabled={!newFact.trim()}>Add</button>
          </form>
          <label className="memall"><input type="checkbox" checked={forEveryone} onChange={(e) => setForEveryone(e.target.checked)} /> The whole office should know this</label>
          {memError && <div className="err" style={{ marginTop: 8 }}>{memError}</div>}
        </div>
        <div className="panel">
          <h3>Company tools {a.name} can use</h3>
          {a.tools.map((t) => (
            <div className="trow" key={t.name}>
              <span>
                <code>{t.name}</code>
                {!t.allowedForUser && <span className="lock">blocked for your login</span>}
                <small>{t.type === 'write' ? 'Changes data' : 'Looks up data'}</small>
              </span>
              <Select label={`Rule for ${t.name}`} value={rule(t)} onChange={(v) => setRule(t.name, v)}
                options={t.type === 'write' ? { ask: RULES.ask, off: RULES.off } : RULES} />
            </div>
          ))}
          {(() => {
            const have = new Set([...a.tools.map((t) => t.name), ...Object.keys((d.tools) || {})]);
            const options = (allTools || []).filter((t) => !have.has(t.name));
            const added = Object.keys(d.tools || {}).filter((n) => !a.tools.some((t) => t.name === n));
            return (
              <>
                {added.map((n) => <div className="trow" key={n}><span><code>{n}</code><small>New · {rule({ name: n, rule: '' }) === 'auto' ? 'Can do alone' : 'Asks you first'}</small></span></div>)}
                {options.length > 0 && (
                  <div className="addmem">
                    <select value={addTool} onChange={(e) => setAddTool(e.target.value)} aria-label="Add a company tool">
                      <option value="">Add a company tool…</option>
                      {options.map((t) => <option key={t.name} value={t.name}>{t.name}{t.type === 'write' ? ' (changes data)' : ''}</option>)}
                    </select>
                    <button className="aio-btn" type="button" disabled={!addTool} onClick={() => {
                      const t = options.find((x) => x.name === addTool);
                      if (t) setRule(t.name, t.type === 'write' ? 'ask' : 'auto');
                      setAddTool('');
                    }}>Add</button>
                  </div>
                )}
              </>
            );
          })()}
          <div className="readonly-note">Tools that change data always ask you first. Press Save to keep changes.</div>
        </div>
        <div className="panel">
          <h3>Brain and voice</h3>
          <div className="trow"><span>AI model<small>Smarter costs more. A change starts a fresh conversation.</small></span><Select label="AI model" value={val('model')} options={MODEL_LABELS} onChange={(v) => set('model', v)} /></div>
          <div className="trow"><span>Voice</span><Select label="Voice" value={val('voice')} options={VOICES} onChange={(v) => set('voice', v)} /></div>
          <div className="trow"><span>Speaks</span><Select label="Language" value={val('language')} options={LANGS} onChange={(v) => set('language', v)} /></div>
        </div>
        <div className="panel">
          <h3>Job instructions</h3>
          <textarea rows={9} style={{ width: '100%' }} aria-label="Job instructions" value={val('instructions')} onChange={(e) => set('instructions', e.target.value)} />
        </div>
      </div>
      {(dirty || saveError) && (
        <div className="panel savebar">
          {saveError ? <span className="err" style={{ flex: 1 }}>{saveError}</span> : <span>You have unsaved changes.</span>}
          <button className="aio-btn ghost" onClick={() => { setDraft(null); setSaveError(null); }} disabled={saving}>Undo</button>
          <button className="aio-btn pri" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      )}
    </div>
  );
}
